import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import api from "../../lib/api";
import { Plus, Pencil, Trash2, X, Bus as BusIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { bus_name: "", route_name: "", operator_id: "" };

export default function BusesPage() {
    const [buses, setBuses] = useState([]);
    const [operators, setOperators] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        const [b, o] = await Promise.all([api.get("/buses"), api.get("/operators")]);
        setBuses(b.data);
        setOperators(o.data);
    };
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setFormOpen(true);
    };
    const openEdit = (b) => {
        setEditing(b);
        setForm({
            bus_name: b.bus_name,
            route_name: b.route_name,
            operator_id: b.operator_id || "",
        });
        setFormOpen(true);
    };
    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, operator_id: form.operator_id || null };
            if (editing) {
                await api.patch(`/buses/${editing.id}`, payload);
                toast.success("Bus updated");
            } else {
                await api.post("/buses", payload);
                toast.success("Bus added");
            }
            setFormOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };
    const remove = async (b) => {
        if (!window.confirm(`Delete ${b.bus_name}?`)) return;
        await api.delete(`/buses/${b.id}`);
        toast.success("Bus deleted");
        load();
    };

    const opById = (id) => operators.find((o) => o.id === id);

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <p className="label-caps mb-2">Fleet</p>
                        <h1 className="font-display text-5xl text-[#111111]">Buses & Routes</h1>
                    </div>
                    <button
                        onClick={openCreate}
                        data-testid="add-bus-button"
                        className="bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] flex items-center gap-2"
                    >
                        <Plus size={18} /> Add Bus
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="buses-grid">
                    {buses.map((b) => (
                        <div key={b.id} className="bg-white border border-[#E5E5E5] p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
                            <div className="flex items-start justify-between mb-6">
                                <div className="w-10 h-10 bg-[#111111] text-white flex items-center justify-center">
                                    <BusIcon size={20} />
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => openEdit(b)}
                                        data-testid={`edit-bus-${b.bus_name}`}
                                        className="p-2 hover:bg-[#111111] hover:text-white"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        onClick={() => remove(b)}
                                        data-testid={`delete-bus-${b.bus_name}`}
                                        className="p-2 hover:bg-[#FF3B30] hover:text-white"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="font-display text-3xl text-[#111111] mb-1">{b.bus_name}</div>
                            <div className="text-sm text-[#666666] mb-4">{b.route_name}</div>
                            <div className="border-t border-[#E5E5E5] pt-4">
                                <p className="label-caps mb-1">Operator</p>
                                <p className="text-sm font-medium">
                                    {opById(b.operator_id)?.name || "— Unassigned —"}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {formOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[#111111] w-full max-w-md animate-slide-up">
                        <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                            <h3 className="font-display text-2xl">{editing ? "Edit" : "Add"} Bus</h3>
                            <button onClick={() => setFormOpen(false)} className="p-1 hover:bg-[#F8F9FA]">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={save} className="p-6 space-y-4">
                            <div>
                                <label className="label-caps block mb-2">Bus Name</label>
                                <input
                                    required
                                    data-testid="bus-name-input"
                                    value={form.bus_name}
                                    onChange={(e) => setForm({ ...form, bus_name: e.target.value })}
                                    className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                    placeholder="Bus 04"
                                />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Route Name</label>
                                <input
                                    required
                                    data-testid="bus-route-input"
                                    value={form.route_name}
                                    onChange={(e) => setForm({ ...form, route_name: e.target.value })}
                                    className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                    placeholder="Route D — East Campus"
                                />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Operator</label>
                                <select
                                    data-testid="bus-operator-select"
                                    value={form.operator_id}
                                    onChange={(e) => setForm({ ...form, operator_id: e.target.value })}
                                    className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                >
                                    <option value="">— None —</option>
                                    {operators.map((o) => (
                                        <option key={o.id} value={o.id}>
                                            {o.name} ({o.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setFormOpen(false)}
                                    className="flex-1 border border-[#111111] font-bold py-3 hover:bg-[#F8F9FA]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="bus-form-submit"
                                    className="flex-1 bg-[#FF3B30] text-white font-bold py-3 hover:bg-[#D70015]"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
